import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import StateDropdown from '../components/StateDropdown';
import * as FiIcons from 'react-icons/fi';

const {FiMapPin,FiUsers,FiArrowRight,FiShield,FiAward,FiHeart,FiExternalLink,FiBookOpen,FiBook,FiUserPlus}=FiIcons;

const HomePage=()=> {
  const features=[
    {
      icon: FiMapPin,
      title: 'State-Specific Resources',
      description: 'Comprehensive family law information tailored to your state\'s requirements.',
      link: '/states'
    },
    {
      icon: FiBookOpen,
      title: 'Legal Glossary',
      description: 'Understanding family law terminology to protect your parental rights.',
      link: 'https://theundefeateddad.com/free-resources#58debb43-b0b9-47e0-972c-a1e38eb043b6',
      isExternal: true
    }
  ];

  const externalResources=[
    {
      title: 'TheUndefeatedDad.com',
      description: 'Your comprehensive resource for father\'s rights and family law guidance.',
      url: 'https://theundefeatedDAD.com',
      buttonText: 'Visit Website',
      className: 'bg-white text-primary-700 border-2 border-primary-600 hover:bg-primary-50'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-hero-gradient">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.6}}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            The Dad Resource Hub
            <span className="text-primary-100 block text-lg sm:text-xl lg:text-2xl font-normal underline decoration-primary-100/60 underline-offset-4 mt-2">
              brought to you by The Undefeated Dad
            </span>
          </motion.h1>
          <motion.p
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.6,delay: 0.2}}
            className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto"
          >
            Your comprehensive guide to understanding family law terminology. Knowledge is power in protecting your rights as a father.
          </motion.p>
          
          {/* Main Action Button */}
          <motion.div
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.6,delay: 0.4}}
            className="flex justify-center mb-6"
          >
            <StateDropdown 
              buttonText="Find Your State Resources"
              className="w-full sm:w-auto"
            />
          </motion.div>

          {/* Three Action Buttons */}
          <motion.div
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.6,delay: 0.6}}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <a
              href="https://theundefeateddad.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-white text-primary-700 font-semibold rounded-lg hover:bg-primary-50 transition-colors shadow-lg border-2 border-white"
            >
              <SafeIcon icon={FiExternalLink} className="mr-2" />
              Visit TheUndefeatedDad.com
            </a>
            
            <a
              href="https://theundefeatedresource.thrivecart.com/theundefeateddad-course/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-secondary-600 text-white font-semibold rounded-lg hover:bg-secondary-700 transition-colors shadow-lg border-2 border-secondary-600"
            >
              <SafeIcon icon={FiBook} className="mr-2" />
              Enroll In The Course
            </a>
            
            <a
              href="https://www.skool.com/the-undefeated-dad-8362"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-accent-600 text-white font-semibold rounded-lg hover:bg-accent-700 transition-colors shadow-lg border-2 border-accent-600"
            >
              <SafeIcon icon={FiUserPlus} className="mr-2" />
              Join The Community
            </a>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Understand Family Law
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our comprehensive platform provides all the information and resources you need to navigate family law matters effectively and protect your rights as a father.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {features.map((feature,index)=> (
              <motion.div
                key={feature.title}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: index * 0.1}}
                className="group bg-card-gradient rounded-xl p-6 hover:bg-white hover:shadow-lg border border-primary-100 hover:border-primary-200 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-600 transition-colors">
                  <SafeIcon icon={feature.icon} className="text-2xl text-primary-600 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                {feature.isExternal ? (
                  <a
                    href={feature.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700"
                  >
                    Learn More
                    <SafeIcon icon={FiExternalLink} className="ml-1 text-sm" />
                  </a>
                ) : (
                  <Link
                    to={feature.link}
                    className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700"
                  >
                    Learn More
                    <SafeIcon icon={FiArrowRight} className="ml-1 text-sm" />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* External Resources Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              The Undefeated Dad Ecosystem
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Access our complete suite of resources designed to empower fathers through every step of their family law journey.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 max-w-md mx-auto">
            {externalResources.map((resource,index)=> (
              <motion.div
                key={resource.title}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{duration: 0.6,delay: index * 0.1}}
                className="bg-white rounded-xl p-6 shadow-lg border border-primary-100 hover:border-primary-200 transition-all duration-300"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{resource.title}</h3>
                <p className="text-gray-600 mb-6">{resource.description}</p>
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors w-full justify-center ${resource.className}`}
                >
                  <SafeIcon icon={FiExternalLink} className="mr-2" />
                  {resource.buttonText}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Standing Strong for Fathers
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We believe every father deserves equal rights and representation in family law matters.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.6}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiShield} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Protection</h3>
              <p className="text-gray-600">Safeguarding your parental rights and ensuring fair representation in all proceedings.</p>
            </motion.div>
            <motion.div
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.6,delay: 0.1}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiAward} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Excellence</h3>
              <p className="text-gray-600">Providing top-tier resources and tools to help you achieve the best outcomes.</p>
            </motion.div>
            <motion.div
              initial={{opacity: 0,y: 20}}
              animate={{opacity: 1,y: 0}}
              transition={{duration: 0.6,delay: 0.2}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiHeart} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Family First</h3>
              <p className="text-gray-600">Always prioritizing the best interests of children and strengthening family bonds.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-hero-gradient relative">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12">
            Trusted by Fathers Nationwide
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">50+</div>
              <div className="text-primary-100">States Covered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">25K+</div>
              <div className="text-primary-100">Fathers Empowered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">98%</div>
              <div className="text-primary-100">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;